/**
 * @module features/devices/components/EditDeviceModal
 * @description Responsive Drawer/Sheet for editing an existing device.
 * On mobile (<640px), opens as an edge-to-edge bottom sheet.
 * On desktop/tablet (>=640px), slides out from the right (480px).
 * Handles attribute updates, lifecycle status transitions, and optional firmware change notes.
 */
import * as React from "react";
import { useForm } from "react-hook-form";
import { AlertCircle, Loader2, Trash2 } from "lucide-react";
import { Drawer } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppError } from "@/lib/api-client";
import { useUpdateDevice } from "../api/useDeviceMutations";
import { DeviceItem, DeviceStatus, DeviceUpdateInput, OS_HASH_REGEX } from "../types";
import { statusLabel } from "./DeviceStatusBadge";

const NEXT_STATUSES: Record<DeviceStatus, DeviceStatus[]> = {
  PROVISIONED: ["ACTIVE", "MAINTENANCE"],
  ACTIVE: ["OFFLINE", "MAINTENANCE"],
  OFFLINE: ["ACTIVE", "MAINTENANCE"],
  MAINTENANCE: ["ACTIVE", "OFFLINE"],
  DECOMMISSIONED: [],
};

interface EditValues {
  device_name: string;
  status: DeviceStatus;
  os_name: string;
  os_version: string;
  os_hash: string;
  notes: string;
}

interface EditDeviceModalProps {
  device: DeviceItem | null;
  open: boolean;
  onClose: () => void;
  onDecommission?: (device: DeviceItem) => void;
}

export function EditDeviceModal({
  device,
  open,
  onClose,
  onDecommission,
}: EditDeviceModalProps) {
  const updateMutation = useUpdateDevice();
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [requestId, setRequestId] = React.useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, dirtyFields },
  } = useForm<EditValues>();

  React.useEffect(() => {
    if (device) {
      reset({
        device_name: device.device_name,
        status: device.status,
        os_name: device.display_module?.os_name ?? "",
        os_version: device.display_module?.os_version ?? "",
        os_hash: device.display_module?.os_hash ?? "",
        notes: "",
      });
      setErrorMessage(null);
      setRequestId(null);
    }
  }, [device, reset]);

  const handleClose = () => {
    setErrorMessage(null);
    setRequestId(null);
    onClose();
  };

  const onSubmit = async (values: EditValues) => {
    if (!device) return;
    setErrorMessage(null);
    setRequestId(null);

    const input: DeviceUpdateInput = {};
    (["device_name", "status", "os_name", "os_version", "os_hash"] as const).forEach((key) => {
      if (dirtyFields[key]) {
        (input as Record<string, string>)[key] = values[key].trim();
      }
    });

    if ((input.os_version || input.os_hash) && values.notes.trim()) {
      input.notes = values.notes.trim();
    }

    if (Object.keys(input).length === 0) {
      handleClose();
      return;
    }

    try {
      await updateMutation.mutateAsync({ id: device.id, input });
      handleClose();
    } catch (err) {
      if (err instanceof AppError) {
        setErrorMessage(err.errorDetails);
        setRequestId(err.requestId);
      } else {
        setErrorMessage("Failed to update device. Please check the inputs.");
      }
    }
  };

  const nextStatuses = device ? NEXT_STATUSES[device.status] ?? [] : [];

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title="Edit Device"
      description={device ? `${device.device_name} (${device.registered_device_id})` : undefined}
    >
      {device && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          {errorMessage && (
            <div
              role="alert"
              className="flex items-start gap-2 p-3 text-xs rounded-lg bg-danger-soft text-danger border border-danger/20"
            >
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p>{errorMessage}</p>
                {requestId && (
                  <p className="mt-1 font-mono text-[11px] text-muted-foreground break-all">
                    Request ID: {requestId}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Device Name */}
          <div className="space-y-1">
            <label htmlFor="edit_device_name" className="text-xs font-medium text-foreground block">
              Device Name <span className="text-destructive">*</span>
            </label>
            <Input
              id="edit_device_name"
              placeholder="e.g. Conference Room 3"
              {...register("device_name", {
                required: "Device name is required.",
                maxLength: { value: 100, message: "Max 100 characters." },
                validate: (v) => v.trim().length > 0 || "Device name is required.",
              })}
              className="h-10 text-sm bg-card"
            />
            {errors.device_name && (
              <p role="alert" className="text-[11px] text-destructive">
                {errors.device_name.message}
              </p>
            )}
          </div>

          {/* Status Selection */}
          <div className="space-y-1">
            <label id="edit_device_status_label" className="text-xs font-medium text-foreground block">
              Operational Status
            </label>
            <Select
              value={watch("status") || device.status}
              onValueChange={(val: DeviceStatus) =>
                setValue("status", val, { shouldDirty: true, shouldValidate: true })
              }
            >
              <SelectTrigger aria-labelledby="edit_device_status_label" className="h-10 bg-card rounded-xl">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={device.status}>
                  {statusLabel(device.status)} (Current)
                </SelectItem>
                {nextStatuses.map((s) => (
                  <SelectItem key={s} value={s}>
                    {statusLabel(s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
              Allowed transitions based on current device state.
            </p>
          </div>

          {/* Display Module Subtype Specifications */}
          {device.device_type === "display_module" && (
            <fieldset className="space-y-3 rounded-xl border border-border/80 bg-muted/20 p-4">
              <legend className="px-1.5 text-xs font-semibold text-foreground">
                Display Module Firmware
              </legend>

              <div className="space-y-1">
                <label htmlFor="edit_device_os_name" className="text-xs font-medium text-foreground block">
                  OS Name <span className="text-destructive">*</span>
                </label>
                <Input
                  id="edit_device_os_name"
                  {...register("os_name", {
                    required: "OS name is required.",
                    maxLength: { value: 100, message: "Max 100 characters." },
                  })}
                  className="h-10 text-sm bg-card"
                />
                {errors.os_name && (
                  <p role="alert" className="text-[11px] text-destructive">
                    {errors.os_name.message}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <label htmlFor="edit_device_os_version" className="text-xs font-medium text-foreground block">
                  OS Version <span className="text-destructive">*</span>
                </label>
                <Input
                  id="edit_device_os_version"
                  {...register("os_version", {
                    required: "OS version is required.",
                    maxLength: { value: 50, message: "Max 50 characters." },
                  })}
                  className="h-10 text-sm bg-card"
                />
                {errors.os_version && (
                  <p role="alert" className="text-[11px] text-destructive">
                    {errors.os_version.message}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <label htmlFor="edit_device_os_hash" className="text-xs font-medium text-foreground block">
                  OS Hash (SHA-256) <span className="text-destructive">*</span>
                </label>
                <Input
                  id="edit_device_os_hash"
                  autoComplete="off"
                  spellCheck={false}
                  {...register("os_hash", {
                    required: "OS hash is required.",
                    validate: (v) =>
                      OS_HASH_REGEX.test(v.trim()) || "Must be a 64-character lowercase hex string.",
                  })}
                  className="h-10 text-xs font-mono bg-card"
                />
                {errors.os_hash && (
                  <p role="alert" className="text-[11px] text-destructive">
                    {errors.os_hash.message}
                  </p>
                )}
              </div>

              <div className="space-y-1 pt-1">
                <label htmlFor="edit_device_notes" className="text-xs font-medium text-foreground block">
                  Firmware Upgrade Notes
                </label>
                <Input
                  id="edit_device_notes"
                  placeholder="e.g. Patching security vulnerability CVE-..."
                  {...register("notes", {
                    maxLength: { value: 500, message: "Max 500 characters." },
                  })}
                  className="h-10 text-sm bg-card"
                />
                <p className="text-[11px] text-muted-foreground">
                  Recorded in firmware upgrade history if version or hash changes.
                </p>
              </div>
            </fieldset>
          )}

          {/* Quick Decommission Action Link */}
          {device.status !== "DECOMMISSIONED" && onDecommission && (
            <div className="rounded-xl border border-border/80 bg-muted/20 p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-medium text-foreground">Decommission Unit</p>
                <p className="text-[11px] text-muted-foreground">
                  Soft-deletes device from active fleet operations
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  handleClose();
                  onDecommission(device);
                }}
                className="h-8 text-xs text-destructive border-destructive/20 hover:bg-destructive/10 hover:text-destructive shrink-0"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Decommission
              </Button>
            </div>
          )}

          {/* Actions Footer */}
          <div className="pt-3 flex items-center justify-end gap-2 border-t border-border mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={updateMutation.isPending}
              className="h-9 px-4 text-xs"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateMutation.isPending}
              className="h-9 px-4 text-xs min-w-[120px] shadow-sm bg-zinc-950 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-white"
            >
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </form>
      )}
    </Drawer>
  );
}
